import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { notEmpty } from '@mahawi/eshop-common/dist/src/not-empty';
import { getTranslation } from '@mahawi/eshop-common/dist/src/translation';
import {
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICustomerProductDetail,
  type IPhoto,
  type IPhotoDimension,
  type IProductList,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { type ILanguageState } from 'customer/react/reducers/language/types';
import { type IUserState } from 'customer/react/reducers/user/types';
import React, { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { load } from '../../reducers/product/reducer';
import FeatureFlags from '../product/feature-flags';
import Stars from '../product/stars';

type GridProps = {
  dispatch: Dispatch;
  User: IUserState;
  Language: ILanguageState;
  products: ICustomerProductDetail[] | IProductList[];
  headerLevel: number;
  onProductClick?: (uuid: string, index: number) => void;
};

function Grid({
  products,
  User,
  Language,
  headerLevel,
  dispatch,
  onProductClick,
}: GridProps): React.JSX.Element[] {
  const { t } = useTranslation();
  const { currencyType } = User;

  const renderedProducts: ReactElement[] = useMemo((): ReactElement[] => {
    return products.map(
      (
        product: ICustomerProductDetail | IProductList,
        index: number,
      ): React.JSX.Element => {
        const currencyPrice: ICurrencyPrice | undefined = product.prices.find(
          (c: ICurrencyPrice): boolean => c.isoCode === currencyType?.isoCode,
        );

        const sortedPhotos: IPhoto[] = [...product.photos].sort(
          (a: IPhoto, b: IPhoto): number => a.position - b.position,
        );

        const photo: IPhoto | undefined =
          sortedPhotos.find(
            (p: IPhoto): boolean => !!(p.dimension && p.dimension.length > 0),
          ) ?? sortedPhotos[0];

        const productName: string | null = getTranslation(
          product.names,
          Language.languageType,
        );

        let srcSet: string = '';
        let dimension: IPhotoDimension | undefined;

        if (photo) {
          srcSet = [300, 200, 100]
            .map(
              (size: number): string =>
                `/static/product/photo/${photo.uuid}--${size}.webp ${size}w`,
            )
            .join(',');

          dimension = photo.dimension
            ? photo.dimension.find(
                ({ type }: IPhotoDimension): boolean => type === '100',
              )
            : undefined;
        }

        const currencyParagraph: string = currencyPrice
          ? `${formatCurrency(
              currencyPrice.pricesWithTax.find(
                (c: ICountryCurrencyPrice): boolean =>
                  c.isoCode === (User.countryType?.isoCode || 'CZE'),
              )?.price || 0,
              currencyType,
            )}`
          : '';

        const stockParagraph: string = product.stock
          ? t('products.countOnStock', { count: product.stock })
          : t('product.outOfStock');

        const descriptionParagraph: string = [currencyParagraph, stockParagraph]
          .filter(notEmpty)
          .join(', ');

        const productHeader: ReactElement =
          headerLevel === 3 ? <h3>{productName}</h3> : <h2>{productName}</h2>;

        const slug: ITranslation | undefined = product.slugs.find(
          (s: ITranslation): boolean => s.code === Language.languageType.code,
        );

        const productLink: string =
          slug && slug.value
            ? `/${Language.languageType.code.toLowerCase()}/p/${slug.value}`
            : `/${Language.languageType.code.toLowerCase()}/product/${product.uuid}`;

        const isOutOfStock: boolean = !product.stock;

        return (
          <Link
            to={productLink}
            key={product.uuid}
            className={`products--link${isOutOfStock ? ' products--link--out-of-stock' : ''}`}
            onClick={(): void => {
              dispatch(
                load({
                  product: {
                    uuid: product.uuid,
                  },
                }),
              );
              onProductClick?.(product.uuid, index);
            }}
          >
            <div className="products--link--inner">
              <div className="products--image--container">
                {photo && (
                  <img
                    alt={productName || ''}
                    title={productName || ''}
                    src={`/static/product/photo/${photo.uuid}--100.webp`}
                    srcSet={srcSet}
                    sizes="(max-width: 600px) calc(50vw - 16px), (max-width: 900px) calc(33vw - 16px), 300px"
                    {...(dimension
                      ? { width: dimension.width, height: dimension.height }
                      : {})}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                  />
                )}
              </div>
              <div className="products--card--content">
                <p>{product.brand?.name}</p>
                {productHeader}
                {product.averageRating != null &&
                  product.reviewCount != null && (
                    <Stars
                      rating={product.averageRating}
                      count={product.reviewCount}
                    />
                  )}
                <FeatureFlags
                  featureFlags={product.featureFlags ?? []}
                  stock={product.stock}
                />
                <p>{descriptionParagraph}</p>
              </div>
            </div>
          </Link>
        );
      },
    );
  }, [products, User, Language, headerLevel, dispatch, t, currencyType]);

  return renderedProducts;
}

const mapStateToProps = ({ User, Language }) => ({
  User,
  Language,
});

export default connect(mapStateToProps)(React.memo(Grid));
